import "react-calendar/dist/Calendar.css";

import React, { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { Button } from "components/ui/Button";
import { Checkbox as CustomCheckbox } from "components/ui/Checkbox";
import { Select } from "components/ui/Select/Select";
import { useAppointment } from "features/appointments/hooks/useAppointment";
import { useStartConsultation } from "features/consultations/hooks/useStartConsultation";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { usePracticeServices } from "features/practice/hooks/usePracticeServices";
import { onInvalidSubmit } from "utils/reportFormErrors";

import style from "./StartConsultationForm.module.css";

const formSchema = z.object({
  type: z
    .string({ required_error: "Enter the type of consultation." })
    .min(1, { message: "Enter the type of consultation." }),
  doctor: z.string(),
  date: z.string(),
  shouldCorelateToTheScheduledAppointment: z.boolean(),
});

export const StartConsultationForm = ({
  closeModal,
  staffName,
  hasAppointment,
  appointmentId,
}) => {
  const patientId = useCurrentPatientId();
  const startConsultationMutation = useStartConsultation(patientId);
  const { data: appointment } = useAppointment(appointmentId);
  const { data: services } = usePracticeServices();
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      doctor: staffName,
      type: "",
      date: dayjs().format("DD.MM.YYYY, HH:mm"),
      shouldCorelateToTheScheduledAppointment: !!hasAppointment,
    },
  });

  const handleFormSubmit = async (values) => {
    startConsultationMutation.mutate(
      {
        serviceId: values.type,
        appointmentId: values?.shouldCorelateToTheScheduledAppointment
          ? appointmentId
          : undefined,
      },
      {
        onSuccess: () => {
          form.reset();
          closeModal();
        },
        onError: () => {
          toast.error("Oops, something went wrong!");
        },
      }
    );
  };

  const shouldCorelateToTheScheduledAppointment = form.watch(
    "shouldCorelateToTheScheduledAppointment"
  );

  useEffect(() => {
    if (services?.length && !form.getValues("type")) {
      const cwConsultation = services.find((s) =>
        s.name?.toLowerCase().includes("cw consultation")
      );
      if (cwConsultation) {
        form.setValue("type", String(cwConsultation.id));
      }
    }
  }, [services, form]);

  useEffect(() => {
    if (
      (shouldCorelateToTheScheduledAppointment,
      appointment,
      appointment?.practiceServiceId)
    ) {
      form.setValue("type", appointment?.practiceServiceId?.toString() ?? "");
    }
  }, [appointment, shouldCorelateToTheScheduledAppointment, form]);

  return (
    <div>
      <FormProvider {...form}>
        <form
          className={style.consultationForm}
          onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("StartConsultationForm"))}
        >
          <div className={style.inputGroupGrid}>
            <FormField name="doctor">
              <label className={style.formLabel}>Doctor</label>
              <input
                type="text"
                className="form-control"
                disabled
                {...form.register("doctor")}
              />
            </FormField>
            <FormField name="date">
              <div className={style.datePickerWrapper}>
                <label className={style.formLabel}>Date</label>
                <input
                  type="text"
                  className="form-control"
                  disabled
                  {...form.register("date")}
                />
              </div>
            </FormField>
          </div>
          <FormField name="type">
            <label className={style.formLabel}>Type</label>
            <Select
              name="type"
              options={[
                { label: "Select...", value: "" },
                ...(services ?? []).map((item) => ({
                  label: item.name,
                  value: String(item.id),
                })),
              ]}
              value={form.watch("type")}
              onChange={(value) => form.setValue("type", value)}
              disabled={shouldCorelateToTheScheduledAppointment}
              placeholder={null}
              fullWidth
            />
          </FormField>
          {hasAppointment && (
            <FormField name="shouldCorelateToTheScheduledAppointment">
              <label className={style.formLabel}>
                <CustomCheckbox
                  {...form.register("shouldCorelateToTheScheduledAppointment")}
                />
                Corelate to the scheduled appointment
              </label>
            </FormField>
          )}
          <div className={style.buttonRow}>
            <Button
              variant="outline"
              size="large"
              onClick={closeModal}
              type="button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="large"
              disabled={startConsultationMutation.isLoading}
            >
              Start
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
